import React from "react";
import { UserContext } from "context/User";
import IconBubble from "components/Account/Wizard/IconBubble";
import { Button, Grid, Paper, Tooltip } from "@mui/material";
import Greeting from "components/utils/Greeting";
import Upload, { UploadType } from "components/utils/Upload";
import { makeAxiosCall } from "utils";
import ResellerInfo from "components/Account/Wizard/ResellerInfo";
import PurchaseMeter from "components/Account/Wizard/PurchaseMeter";
import SuccessAni from "components/utils/SuccessAni";
import { states } from "interfaces/State";
import "scss/components/Account/Wizard/Wizard.scss";
import Loading from "components/utils/Loading";
import logo from "assets/images/logo.svg";
import { useNavigate } from "react-router-dom";
import { addressCoverage, contactCoverage } from "utils/coverage";
import { isInSecondChance, SECOND_CHANCE_MARKER, secondChanceLockdown } from "utils/secondChance";

interface WizardProps {
    hideNav?: boolean,
}

type WizardView = "none" | "info" | "cert" | "meter" | "agreement";

const Wizard = ({ hideNav = false }: WizardProps) => {
    const { currentUser, signOut, fetchCurrentUser } = React.useContext(UserContext);
    const navigate = useNavigate();
    const [view, setView] = React.useState<WizardView>("none");
    const [stepsComplete, setStepsComplete] = React.useState<boolean>(false);
    const [loadingDocusign, setLoadingDocusign] = React.useState<boolean>(false);
    const [meterMet, setMeterMet] = React.useState<boolean>(false);
    // Agreement state, probed before the user clicks so the button can say
    // "Continue" and so we can warn when a colleague already started it.
    type AgreementStatus = {
        mode: "new" | "continue" | "other_signer" | "already_completed" | "unknown";
        canStartOver?: boolean;
        startedBy?: { name: string; email: string };
    };
    const [agreementStatus, setAgreementStatus] = React.useState<AgreementStatus | null>(null);

    const approvalStatus = currentUser.company.approvalStatus;

    // Account Setup is complete only once the company actually has full required
    // address + contact type coverage AND a designated default billing address +
    // contact (a separate designation from the typed Billing address/contact, though
    // it can be autofilled from them). We do NOT treat "finished/exited the guided
    // tour" (the accountTourDone localStorage flag) as completion — exiting the tour
    // shouldn't mark the step done when the required data isn't there.
    const accountSetupDone =
        addressCoverage(currentUser.company.addresses).complete &&
        contactCoverage(currentUser.company.contacts).complete &&
        !!currentUser.company.defaultBillingAddress &&
        !!currentUser.company.defaultBillingContact;

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const resellerAgreementQuery = urlParams.get("ra");
        const resellerAgreementCompleteQuery = urlParams.get("event");
        // Returning from placing an onboarding order — open the meter/progress view.
        if (urlParams.get("onboardingStep") === "meter") {
            setView("meter");
        }

        if (currentUser && currentUser.company.id) {
            if (resellerAgreementQuery === "269394jdiepow494" || resellerAgreementCompleteQuery == "signing_complete") {
                setView("none");

                if (!approvalStatus.agreementComplete) {
                    // The API verifies with DocuSign that the reseller recipient
                    // actually signed before marking the agreement complete and
                    // clearing any re-contract expiration. (Home.tsx fires the same
                    // call for approved resellers, where this Wizard never mounts;
                    // the endpoint is idempotent so the overlap is harmless.)
                    makeAxiosCall("post", "docusign/confirm-signing").then(() => {
                        fetchCurrentUser();
                    });
                }
            }
        }
        fetchCurrentUser();
        loadMeter();
    }, []);

    React.useEffect(() => {
        if (
            approvalStatus.agreementComplete &&
            approvalStatus.certificateUploaded &&
            approvalStatus.infoFormComplete
        ) {
            setStepsComplete(true);
        }
    }, [currentUser]);

    const loadMeter = async () => {
        try {
            const res = await makeAxiosCall("get", "sales-orders/purchase-meter");
            if (res && res.data) setMeterMet(!!res.data.met);
        } catch (e) {
            // non-fatal; agreement just stays locked until the meter is met
        }
    };

    const createDocusign = async (startOver = false) => {
        setLoadingDocusign(true);
        try {
            if (
                currentUser.company &&
                currentUser.company.fishbowlCustomerId &&
                approvalStatus.infoFormComplete &&
                currentUser.company.contacts.length > 0 &&
                currentUser.company.addresses.length > 0
            ) {
                const paymentTerms = await makeAxiosCall(
                    "get",
                    `paymentterms/${currentUser.company.fishbowlCustomerId}`,
                    null,
                    { headers: { "Content-Type": "application/json", } },
                );

                let primaryContact = currentUser.company.contacts.find((contact) => {
                    return contact.contactTypes.some((type) => type.name == "Primary");
                });

                if (!primaryContact) {
                    primaryContact = currentUser.company.contacts[0];
                }

                let primaryAddress = currentUser.company.addresses.find((address) => {
                    return address.addressTypes.some((type) => type.name == "Primary");
                });

                if (!primaryAddress) {
                    primaryAddress = currentUser.company.addresses[0];
                }

                let firstName = currentUser.firstName;
                if (!firstName || firstName.trim().length < 2) firstName = primaryContact.firstName;

                let lastName = currentUser.lastName;
                if (!lastName || lastName.trim().length < 2) lastName = primaryContact.lastName;

                let state = primaryAddress.state;

                if (state.length == 2) {
                    const stateObject = states.find((st) => st.code == state);

                    if (stateObject) state = stateObject.name;
                }

                let companyName = currentUser.company.resellerInfo.legalEntityName;
                if(currentUser.company.resellerInfo.dbas != null && currentUser.company.resellerInfo.dbas.length > 0) {
                    companyName += " DBA " + currentUser.company.resellerInfo.dbas;
                }

                let docusignData = {
                    resellerEmail: currentUser.email,
                    resellerName: firstName + " " + lastName,
                    currentUserId: currentUser.uid,
                    companyId: currentUser.company.id,
                    companyName: companyName,
                    resellerTitle: primaryContact.title,
                    businessType: currentUser.company.resellerInfo.businessType,
                    city: primaryAddress.city,
                    state: state,
                    address: (primaryAddress.streetAddress + (primaryAddress.streetAddressTwo ? (", " + primaryAddress.streetAddressTwo) : "")),
                    zip: primaryAddress.zip,
                    country: primaryAddress.country,
                    paymentTerms: paymentTerms.data
                }


                let response = await makeAxiosCall(
                    "post",
                    `docusign/create-agreement`,
                    { ...docusignData, startOver },
                    { headers: { "Content-Type": "application/json", } },
                );

                if (response) {
                    if (response.data && response.data.blocked) {
                        setAgreementStatus({
                            mode: response.data.mode,
                            canStartOver: response.data.canStartOver,
                            startedBy: response.data.startedBy,
                        });
                    }
                    return response.data;
                } return false;
            }
        } catch (error) {
            // Auto-notification handles 500/network errors
        } finally {
            setLoadingDocusign(false);
        }
    };

    const buildCompany = () => {
        return (
            <>
                <SuccessAni title="Thank You" />

                <p style={{ fontSize: 16, paddingBottom: 10 }}>
                    We&apos;re currently reviewing your account and will email you with further instructions.
                </p>
                <Button
                    type="submit"
                    style={{ width: "100%" }}
                    variant="contained"
                    color="primary"
                    className="btn normal"
                    onClick={() => {
                        window.location.href = "https://blizzardpro.com";
                    }}
                >
                    Go to blizzardpro.com
                </Button>
            </>
        );
    };

    const buildResellerCertificateForm = () => {
        return (
            <div style={{ maxWidth: 600, margin: "auto", textAlign: "center" }}>
                <Button
                    style={{ float: "left" }}
                    onClick={() => {
                        setView("none");
                        fetchCurrentUser();
                    }}
                >
                    Back
                </Button>
                <Upload
                    description="Please complete and submit the form below:"
                    maxFileSize={10000000}
                    uploadType={UploadType.cert}
                    onFinished={() => {
                        setView("none");
                        fetchCurrentUser();
                    }}
                    successMessage={
                        "Your resale certificate has been submitted and is pending Blizzard approval."
                    }
                />
            </div>
        );
    };

    const buildMeterView = () => {
        return (
            <div style={{ maxWidth: 700, margin: "auto" }}>
                <Button
                    style={{ float: "left" }}
                    onClick={() => {
                        setView("none");
                        loadMeter();
                        fetchCurrentUser();
                    }}
                >
                    Back
                </Button>
                <PurchaseMeter onMetChange={setMeterMet} />
            </div>
        );
    };

    // Probe agreement state when this view opens. Best-effort: on any failure we
    // fall through to the neutral label rather than blocking the wizard.
    React.useEffect(() => {
        if (view !== "agreement") return;
        let cancelled = false;
        (async () => {
            try {
                const res = await makeAxiosCall("get", `docusign/agreement-status`);
                if (!cancelled && res && res.data) setAgreementStatus(res.data);
            } catch (error) {
                if (!cancelled) setAgreementStatus({ mode: "unknown" });
            }
        })();
        return () => { cancelled = true; };
    }, [view]);

    const buildResellerAgreementForm = () => {
        return (
            <div style={{
                width: "100%",
                height: 400,
                maxWidth: 800,
                margin: "0 auto",
                marginTop: "20%",
            }}>
                <h2>{ loadingDocusign ? "Building Reseller Agreement Document..." : "You're leaving the portal."}</h2>
                {
                    loadingDocusign ? <Loading height="40vh" title="" position="relative" /> :
                        <p style={{ fontSize: 16, paddingBottom: 10 }}>
                            We are redirecting you to the Docusign system in order for you to
                            securely review and sign your Reseller Agreement. You will be
                            returned to the portal once you&apos;ve completed the e-signature process.
                        </p>
                }

                {
                    agreementStatus?.mode === "other_signer" && !loadingDocusign &&
                    <p style={{ fontSize: 16, paddingBottom: 10 }}>
                        <strong>{agreementStatus.startedBy?.name}</strong>
                        {agreementStatus.startedBy?.email ? ` (${agreementStatus.startedBy.email})` : ""}
                        {" "}already started this agreement and it&apos;s waiting on their signature.
                        Ask them to finish it, or start a new agreement in your name.
                        Starting over cancels theirs.
                    </p>
                }
                {
                    agreementStatus?.mode === "already_completed" && !loadingDocusign &&
                    <p style={{ fontSize: 16, paddingBottom: 10 }}>
                        This agreement has already been signed. If you think that&apos;s wrong,
                        contact support.
                    </p>
                }

                <div className="btn-group">
                    <Button
                        type="submit"
                        style={{ width: "100%" }}
                        variant="contained"
                        color="secondary"
                        className="btn"
                        disabled={loadingDocusign}
                        onClick={() => {
                            setView("none");
                        }}
                    >
                        Close
                    </Button>
                    {
                        agreementStatus?.mode !== "already_completed" &&
                        <Button
                            type="submit"
                            style={{ width: "100%" }}
                            variant="contained"
                            color="primary"
                            className="btn"
                            disabled={loadingDocusign}
                            onClick={() => {
                                const startOver = agreementStatus?.mode === "other_signer";
                                createDocusign(startOver).then((docData) => {
                                    if (docData && docData.redirectUrl)
                                        window.location.href = docData.redirectUrl;
                                });
                            }}
                        >
                            {
                                agreementStatus?.mode === "continue" ? "Continue" :
                                    agreementStatus?.mode === "other_signer" ? "Start a New Agreement" :
                                        "Let's Go"
                            }
                        </Button>
                    }
                </div>
            </div>
        );
    };

    const buildDocumentsUnderReview = () => {
        return (
            <div style={{ marginTop: 20 }}>
                <SuccessAni title="Thank You" />

                <p style={{ fontSize: 16, paddingBottom: 10 }}>
                    We&apos;re currently reviewing the information and documents you&apos;ve provided, and will contact you when finished.
                </p>
                <h3>
                    Typical Wait Time:{" "}
                    <span style={{ color: "#282bf6" }}>1 business day</span>
                </h3>
            </div>
        );
    };

    const buildInfoFormButtonText = () => {
        if (approvalStatus.infoFormDenialReason) return "Update";
        else if (approvalStatus.infoFormApproved) return "Approved";
        else if (approvalStatus.infoFormComplete) return "Submitted";
        else return "Start";
    };

    const buildCertButtonText = () => {
        if (approvalStatus.certificateDenialReason) return "Update";
        else if (approvalStatus.certificateUploaded || approvalStatus.certificateApproved) return "Upload More";
        else return "Upload";
    };

    const buildAgreementButtonText = () => {
        if (approvalStatus.agreementDenialReason) return "Update";
        else if (approvalStatus.agreementApproved) return "Approved";
        else if (approvalStatus.agreementComplete) return "Submitted";
        else return "Review & Sign";
    };

    // --- 5-step onboarding sequence ---
    const renderStepCard = (
        n: number,
        title: string,
        icon: string,
        description: string,
        complete: boolean,
        denied: boolean,
        buttonText: string,
        onClick: () => void,
        opts?: { locked?: boolean; lockedReason?: string; disabled?: boolean; btnClass?: string; size?: object },
    ) => {
        const locked = !!opts?.locked;
        return (
            <Grid size={opts?.size || { xs: 12, md: 6, lg: 4 }} key={n}>
                <Paper elevation={3} className="wizard-navigation-card">
                    <div className="wizard-step-number">{n}</div>
                    <IconBubble complete={complete} denied={denied} icon={icon} />
                    <h3>{title}</h3>
                    <p>{description}</p>
                    <Tooltip title={locked ? (opts?.lockedReason || "") : ""}>
                        <span>
                            <Button
                                onClick={onClick}
                                variant="contained"
                                color="primary"
                                style={{ width: "100%", marginBottom: 20 }}
                                disabled={locked || !!opts?.disabled}
                                className={denied ? "btn red" : (complete ? "btn green" : "btn")}
                            >
                                {locked ? <><i className="fad fa-lock" style={{ marginRight: 8 }}></i>{buttonText}</> : buttonText}
                            </Button>
                        </span>
                    </Tooltip>
                </Paper>
            </Grid>
        );
    };

    // A reseller serving their one-time reactivation window: lead with the hard
    // deadline instead of the raw expiration reason (which carries the internal
    // "(Second Chance)" marker), and tell bucket A why the catalog is dark.
    const inSecondChance = isInSecondChance(currentUser.company);
    const catalogLocked = secondChanceLockdown(currentUser.company);

    const buildSecondChanceNotice = () => {
        const deadline = approvalStatus.expirationDate
            ? new Date(approvalStatus.expirationDate).toLocaleDateString("en-US", {
                // The date is midnight UTC — without an explicit zone US browsers
                // render it one day early, disagreeing with the reminder emails.
                timeZone: "UTC", year: "numeric", month: "long", day: "numeric",
            })
            : null;
        const reason = (approvalStatus.expirationReason || "").replace(SECOND_CHANCE_MARKER, "").trim();

        return (
            <div className="second-chance-notice">
                {reason && <h3>{reason}</h3>}
                <h3>
                    Your access has been restored through{" "}
                    <span style={{ color: "#282bf6" }}>{deadline}</span>. Complete the steps
                    below before then to keep your reseller account — this is a one-time extension.
                </h3>
                {catalogLocked && (
                    <p style={{ fontSize: 15 }}>
                        <i className="fad fa-lock" style={{ marginRight: 8 }}></i>
                        Products and orders stay locked until your reseller agreement is signed.
                    </p>
                )}
            </div>
        );
    };

    const launchAccountTour = () => {
        navigate("/company?onboardingTour=1&tab=Members");
    };

    const buildNavigationButtons = () => {
        // Bucket A (re-contract round) is exempt from the $2,500 purchase meter:
        // hide that step and don't let it gate signing. B/C and brand-new resellers
        // (no recontract_bucket) still go through the meter.
        const isBucketA = currentUser.company.recontractBucket === "A";
        // Strict sequential gating: a step can only be started once EVERY prior step
        // is complete — cumulative, not just the immediate predecessor. This matters
        // because the purchase meter (meterMet) auto-fills from existing qualifying
        // orders, so it can read "complete" on its own; a non-cumulative check would
        // let that unlock signing even when account setup isn't finished.
        const through1 = !!approvalStatus.infoFormComplete;
        const through2 = through1 && !!approvalStatus.certificateUploaded;
        const through3 = through2 && accountSetupDone;
        const through4 = through3 && (isBucketA || meterMet);
        const agreementLocked = isBucketA ? !through3 : !through4;
        const agreementLockedReason = !through3
            ? "Complete the earlier steps first."
            : "Place your initial qualifying order to unlock signing.";
        return (
            <Grid container spacing={4} className="wizard-navigation-button-container">
                {renderStepCard(
                    1, "Reseller Information Form", "fad fa-address-card",
                    approvalStatus.infoFormDenialReason || "Tell us the basics about your business.",
                    approvalStatus.infoFormComplete, !!approvalStatus.infoFormDenialReason,
                    buildInfoFormButtonText(), () => setView("info"),
                )}

                {renderStepCard(
                    2, "Upload Resale Certificate", "fad fa-file-certificate",
                    approvalStatus.certificateDenialReason || "Submit your resale certificate for approval.",
                    approvalStatus.certificateUploaded, !!approvalStatus.certificateDenialReason,
                    buildCertButtonText(), () => setView("cert"),
                    {
                        locked: !through1,
                        lockedReason: "Complete the Reseller Information Form first.",
                    },
                )}

                {renderStepCard(
                    3, "Account Setup", "fad fa-users-cog",
                    "Add your team, addresses, contacts, and billing in a quick guided walkthrough.",
                    accountSetupDone, false,
                    accountSetupDone ? "Revisit Setup" : "Start Setup", launchAccountTour,
                    {
                        locked: !through2,
                        lockedReason: "Upload your resale certificate first.",
                    },
                )}

                {!isBucketA && renderStepCard(
                    4, "Make Your First Purchase", "fad fa-tachometer-alt-fast",
                    "Place your initial qualifying order to activate your reseller account.",
                    // Don't show "complete" until the step is actually reachable, even
                    // though the meter may already be met from prior orders.
                    through3 && meterMet, false,
                    "View Progress", () => setView("meter"),
                    {
                        locked: !through3,
                        lockedReason: "Finish account setup first.",
                        // Bottom row of the 5-step layout: two cards split full width.
                        size: { xs: 12, md: 6, lg: 6 },
                    },
                )}

                {renderStepCard(
                    isBucketA ? 4 : 5, "Sign Reseller Agreement", "fad fa-file-alt",
                    approvalStatus.agreementDenialReason || "Review and sign our reseller agreement to finish.",
                    approvalStatus.agreementComplete, !!approvalStatus.agreementDenialReason,
                    buildAgreementButtonText(), () => setView("agreement"),
                    {
                        locked: agreementLocked,
                        lockedReason: agreementLockedReason,
                        disabled: approvalStatus.agreementComplete || approvalStatus.agreementApproved,
                        // Bucket A: lone final card spans full width. Otherwise it's the
                        // second card on the 5-step bottom row (half width).
                        size: isBucketA ? { xs: 12, md: 12, lg: 12 } : { xs: 12, md: 6, lg: 6 },
                    },
                )}
            </Grid>
        );
    };

    const buildBodyOfTheWizard = () => {
        if (!currentUser.company) {
            return buildCompany();
        } else if (view === "info") {
            return <ResellerInfo closeForm={() => {
                fetchCurrentUser();
                setView("none");
            }} />;
        } else if (view === "cert") {
            return buildResellerCertificateForm();
        } else if (view === "meter") {
            return buildMeterView();
        } else if (view === "agreement") {
            return buildResellerAgreementForm();
        } else {
            return <></>;
        }
    };

    return (
        <Grid container className="wizard">
            <img className="logo-top" src={logo} alt="Blizzard" />

            {!hideNav && (
                <div className="top-floating-nav">
                    <div
                        className="back-crumb"
                        onClick={() => {
                            fetchCurrentUser();
                        }}
                    >
                        <i className="fad fa-reload"></i> Refresh
                    </div>
                    <div
                        className="back-crumb"
                        onClick={() => {
                            signOut();
                        }}
                    >
                        <i className="fad fa-sign-out"></i> Log Out
                    </div>
                </div>
            )}

            <Grid size={{ xs: 12 }}>
                {
                    buildBodyOfTheWizard()
                }
            </Grid>

            {
                view !== "none" ? <></> :
                    <Grid size={{ xs: 12 }}>
                        <Grid container>
                            <Grid size={{ xs: 12 }}>
                                <div className="intro">
                                    <div className="top-intro">
                                        <Greeting />
                                        {!stepsComplete ? <h2>Let&apos;s get your account set up.</h2> : buildDocumentsUnderReview()}
                                        {!stepsComplete && approvalStatus?.expirationDate && (
                                            inSecondChance ? buildSecondChanceNotice() : <h3>{approvalStatus?.expirationReason}</h3>
                                        )}
                                    </div>
                                </div>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                {buildNavigationButtons()}
                            </Grid>
                        </Grid>
                    </Grid>
            }
        </Grid>
    );
};

export default Wizard;
